import React, { useState, useRef, useEffect } from 'react';
import {
  Box, TextField, IconButton, List, ListItem, ListItemText, Typography, 
  Avatar, Paper, Button, Stack, Chip, CircularProgress,
  Card, CardContent, Divider, useMediaQuery
} from '@mui/material';
import {
  SendOutlined as SendIcon,
  ChatOutlined as ChatIcon,
  UpdateOutlined as UpdateIcon,
  SmartToyOutlined as AIIcon,
  AddCircleOutline as AddIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';
import { useTheme } from '@emotion/react';
import { sendMessage } from 'src/services/ClaudeService.js';
import alfie from 'src/assets/alfie.png';
import ReactMarkdown from 'react-markdown';

const LessonChat = ({ 
  lessonContent, 
  setLessonContent, 
  unitDay, 
  setHighlightedChanges,
  addBlockToLessonPlan,
  isOpen,
  onToggle 
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [addedBlocks, setAddedBlocks] = useState(new Set());
  const chatRef = useRef(null);
  const inputRef = useRef(null);

  // Suggested questions based on the lesson
  const suggestedQuestions = [
    "How can I make this lesson more engaging?",
    "What activities would work well for visual learners?",
    "How can I differentiate this for struggling students?",
    "What questions should I ask to check understanding?",
    "Can you add a 10-minute group activity?",
    "How can I connect this to real-world examples?"
  ];

  // Function to parse AI response into addable blocks
  const parseResponseIntoBlocks = (responseText) => {
    console.log('Parsing response:', responseText);
    
    // Split by double newlines to get potential blocks
    const sections = responseText.split(/\n\s*\n/);
    const blocks = [];
    
    sections.forEach((section, sectionIndex) => {
      const lines = section.trim().split('\n');
      if (lines.length === 0 || lines[0].trim() === '') return;
      
      // Determine block type based on first line
      const firstLine = lines[0].trim();
      let blockType = 'paragraph';
      let content = section.trim();
      
      if (firstLine.startsWith('# ')) {
        blockType = 'heading';
      } else if (firstLine.startsWith('## ') || firstLine.startsWith('### ')) {
        blockType = 'subheading';
      } else if (firstLine.match(/^[-*•○]\s/) || firstLine.match(/^\d+\.\s/)) {
        blockType = 'list';
      } else if (firstLine.includes(':') && lines.length > 1) {
        // Check if this looks like a titled section (e.g., "Incorporate Technology: description...")
        blockType = 'section';
      }
      
      // Create block
      blocks.push({
        id: `block-${blocks.length}`,
        type: blockType,
        content: content,
        preview: content.substring(0, 80) + (content.length > 80 ? '...' : '')
      });
    });
    
    console.log('Created blocks:', blocks);
    return blocks;
  };

  // Initialize with welcome message and suggested questions
  useEffect(() => {
    if (messages.length === 0) {
      setMessages([
        {
          id: 1,
          text: `Hi! I'm here to help you improve Unit 5.0 Day ${unitDay}. I can suggest modifications, activities, or answer questions about your lesson plan. What would you like to work on?`,
          sender: 'ai',
          timestamp: new Date()
        }
      ]);
    }
  }, [unitDay, messages.length]);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [messages, isLoading]);

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = {
      id: Date.now(),
      text: inputMessage,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      // Create context-aware prompt
      const contextPrompt = `
        I'm a teacher working on Unit 5.0 Day ${unitDay}. Here's my current lesson plan:
        
        ${lessonContent}
        
        Teacher's question: ${inputMessage}
        
        Please provide helpful suggestions for improving or modifying this lesson plan. Be specific and actionable.
      `;

      const response = await sendMessage(contextPrompt);
      
      // Handle response object or string
      const responseText = typeof response === 'object' ? response.response : response;
      const followUps = typeof response === 'object' ? response.followUps || [] : [];
      
      // Parse the AI response into addable blocks
      const blocks = parseResponseIntoBlocks(responseText);
      console.log('Parsed blocks:', blocks);
      
      const aiMessage = {
        id: Date.now() + 1,
        text: responseText,
        sender: 'ai',
        timestamp: new Date(),
        canUpdateLesson: blocks.length === 0, // Only show "Update Lesson Plan" button if no blocks detected
        followUps: followUps, // Add follow-up questions
        showBlocks: blocks.length > 0, // Show blocks if we detected any
        blocks: blocks
      };

      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = {
        id: Date.now() + 1,
        text: "I'm having trouble responding right now. Please try again.",
        sender: 'ai',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuggestedQuestion = (question) => {
    setInputMessage(question);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleUpdateLessonPlan = async (aiResponse) => {
    console.log('Updating lesson plan with:', aiResponse);
    
    try {
      // Call the backend to get the updated lesson plan with changes
      const contextPrompt = `
        Original lesson plan:
        ${lessonContent}
        
        Requested changes:
        ${aiResponse}
        
        Please provide the updated lesson plan incorporating these changes.
      `;

      const response = await sendMessage(contextPrompt);
      const aiResponse = typeof response === 'object' ? response.response : response;
      
      // Parse the AI response into addable blocks
      const blocks = parseResponseIntoBlocks(aiResponse);
      
      // Show AI response with addable blocks
      const blockMessage = {
        id: Date.now(),
        text: aiResponse,
        sender: 'ai',
        timestamp: new Date(),
        showBlocks: true,
        blocks: blocks
      };
      
      setMessages(prev => [...prev, blockMessage]);
    } catch (error) {
      console.error('Error updating lesson plan:', error);
      const errorMessage = {
        id: Date.now(),
        text: "Sorry, I couldn't update the lesson plan right now. Please try again.",
        sender: 'ai',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    }
  };

  // Handle adding a block to the lesson plan
  const handleAddBlock = (block) => {
    if (addBlockToLessonPlan && !addedBlocks.has(block.id)) {
      addBlockToLessonPlan(block);
      
      // Mark this block as added
      setAddedBlocks(prev => new Set([...prev, block.id]));
      
      // Show confirmation message
      const confirmMessage = {
        id: Date.now(),
        text: `✅ Added "${block.preview}" to your lesson plan.`,
        sender: 'ai',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, confirmMessage]);
    }
  };

  // Enhanced markdown component that adds plus buttons inline
  const createEnhancedMarkdown = (text, blocks) => {
    if (!blocks || blocks.length === 0) {
      return <ReactMarkdown components={chatMarkdownComponents}>{text}</ReactMarkdown>;
    }

    // Split the text and manually render with buttons at the right positions
    let renderedBlocks = [];
    let textPosition = 0;

    // Sort blocks by their position in the text
    const sortedBlocks = [...blocks].sort((a, b) => {
      const posA = text.indexOf(a.content.substring(0, 30));
      const posB = text.indexOf(b.content.substring(0, 30));
      return posA - posB;
    });

    sortedBlocks.forEach((block, index) => {
      const blockStart = text.indexOf(block.content, textPosition);
      
      if (blockStart !== -1) {
        // Add text before this block (if any)
        if (blockStart > textPosition) {
          const beforeText = text.substring(textPosition, blockStart);
          if (beforeText.trim()) {
            renderedBlocks.push(
              <ReactMarkdown key={`before-${index}`} components={chatMarkdownComponents}>
                {beforeText}
              </ReactMarkdown>
            );
          }
        }

        // Skip plus button for the first block (index 0) as it's just an explainer
        const isAdded = addedBlocks.has(block.id);
        const isFirstBlock = index === 0;
        
        if (isFirstBlock) {
          // First block: just render content without plus button
          renderedBlocks.push(
            <Box key={block.id} sx={{ my: 1 }}>
              <ReactMarkdown components={chatMarkdownComponents}>
                {block.content}
              </ReactMarkdown>
            </Box>
          );
        } else {
          // Other blocks: render with plus button
          renderedBlocks.push(
            <Box key={block.id} sx={{ position: 'relative', display: 'flex', alignItems: 'flex-start', gap: 1, my: 1 }}>
              {/* Plus/Check button - minimal style */}
              <IconButton
                size="small"
                onClick={() => handleAddBlock(block)}
                disabled={isAdded}
                sx={{
                  width: 20,
                  height: 20,
                  bgcolor: isAdded ? 'black' : 'black',
                  color: isAdded ? 'white' : 'white',
                  marginLeft: -6.5,
                  marginTop: 0.75,
                  border: `1px solid black`,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: 'black',
                    color: 'white',
                    transform: 'scale(1.05)',
                    boxShadow: 1
                  },
                  '&:disabled': {
                    bgcolor: 'black',
                    color: 'white',
                    opacity: 0.8
                  },
                  zIndex: 2
                }}
              >
                {isAdded ? (
                  <CheckIcon fontSize="small" />
                ) : (
                  <AddIcon fontSize="small" />
                )}
              </IconButton>

              {/* Block content */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <ReactMarkdown components={chatMarkdownComponents}>
                  {block.content}
                </ReactMarkdown>
              </Box>
            </Box>
          );
        }

        textPosition = blockStart + block.content.length;
      }
    });

    // Add any remaining text after the last block
    if (textPosition < text.length) {
      const remainingText = text.substring(textPosition);
      if (remainingText.trim()) {
        renderedBlocks.push(
          <ReactMarkdown key="remaining" components={chatMarkdownComponents}>
            {remainingText}
          </ReactMarkdown>
        );
      }
    }

    return (
      <Box sx={{ position: 'relative', pl: 2, backgroundColor: 'transparent' }}>

        {/* Rendered content with inline buttons */}
        <Box sx={{ backgroundColor: 'transparent' }}>
          {renderedBlocks}
        </Box>
      </Box>
    );
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Markdown components for chat styling
  const chatMarkdownComponents = {
    h1: ({ children }) => (
      <Typography variant="h6" sx={{ fontWeight: 700, mt: 3, mb: 1.5, fontSize: '1.1rem' }}>
        {children}
      </Typography>
    ),
    h2: ({ children }) => (
      <Typography variant="h6" sx={{ fontWeight: 600, mt: 2.5, mb: 1.5, fontSize: '1.05rem' }}>
        {children}
      </Typography>
    ),
    h3: ({ children }) => (
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2, mb: 1, fontSize: '1rem' }}>
        {children}
      </Typography>
    ),
    p: ({ children }) => (
      <Typography 
        variant="body1" 
        sx={{ 
          mb: 1.5, 
          fontSize: '1rem',
          lineHeight: 1.6,
          wordBreak: 'break-word',
          overflowWrap: 'break-word'
        }}
      >
        {children}
      </Typography>
    ),
    strong: ({ children }) => (
      <Typography component="span" sx={{ fontWeight: 700, display: 'inline' }}>
        {children}
      </Typography>
    ),
    ul: ({ children, ...props }) => {
      const depth = props.depth || 0;
      return (
        <Box 
          component="div" 
          sx={{ 
            pl: depth > 0 ? 1.5 : 0, 
            mb: 1.5, 
            mt: 0.5
          }}
        >
          {children}
        </Box>
      );
    },
    ol: ({ children, ...props }) => {
      const depth = props.depth || 0;
      return (
        <Box 
          component="div" 
          sx={{ 
            pl: depth > 0 ? 1.5 : 0, 
            mb: 1.5, 
            mt: 0.5
          }}
        >
          {children}
        </Box>
      );
    },
    li: ({ children, ...props }) => {
      // Detect nesting level by checking parent elements
      const isNested = props.index !== undefined && props.index > 0;
      const bulletChar = isNested ? '◦' : '•';
      
      return (
        <Typography 
          component="div" 
          variant="body1" 
          sx={{ 
            mb: 1, 
            fontSize: '1rem',
            lineHeight: 1.6,
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
            position: 'relative',
            paddingLeft: '20px',
            '&::before': {
              content: `"${bulletChar}"`,
              position: 'absolute',
              left: 0,
              color: 'text.secondary',
              fontWeight: 'bold'
            },
            // Nested bullets - detect by checking if parent has bullet
            '.MuiBox-root &': {
              '&::before': {
                content: '"◦"',
                color: 'text.disabled'
              }
            }
          }}
        >
          {children}
        </Typography>
      );
    },
    code: ({ children }) => (
      <Typography 
        component="code" 
        sx={{ 
          backgroundColor: 'grey.100', 
          padding: '2px 4px', 
          borderRadius: 1, 
          fontSize: '0.9rem',
          fontFamily: 'monospace'
        }}
      >
        {children}
      </Typography>
    ),
    pre: ({ children }) => (
      <Box 
        component="pre" 
        sx={{ 
          backgroundColor: 'grey.100', 
          padding: 1, 
          borderRadius: 1, 
          overflow: 'auto',
          fontSize: '0.9rem',
          fontFamily: 'monospace',
          mb: 1
        }}
      >
        {children}
      </Box>
    )
  };

  // Desktop: Always show as sidebar, Mobile: Show as popup
  if (!isOpen && isMobile) {
    return (
      <Button
        variant="contained"
        startIcon={<ChatIcon />}
        onClick={onToggle}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          borderRadius: '24px',
          px: 3,
          py: 1.5,
          boxShadow: theme.shadows[8],
          zIndex: 1000
        }}
      >
        Ask AI About This Lesson
      </Button>
    );
  }

  // Don't render anything if closed on desktop (will be handled by parent)
  if (!isOpen && !isMobile) {
    return null;
  }

  return (
    <Box
      sx={{
        // Mobile: Fixed popup
        ...(isMobile && {
          position: 'fixed',
          bottom: 24,
          right: 24,
          width: 400,
          height: 600,
          zIndex: 1000,
          borderRadius: '16px',
          boxShadow: theme.shadows[8]
        }),
        // Desktop: Static sidebar (no border radius, fills container)
        ...(!isMobile && {
          width: '100%',
          height: '100%',
          position: 'static'
        }),
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        backgroundColor: theme.palette.background.paper
      }}
    >
      {/* Header - only for mobile */}
      {isMobile && (
        <Box sx={{ 
          p: 2, 
          bgcolor: 'primary.main', 
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AIIcon />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Lesson Assistant
            </Typography>
          </Box>
          <IconButton onClick={onToggle} sx={{ color: 'white' }}>
            ×
          </IconButton>
        </Box>
      )}

      {/* Messages - using Search.js style */}
      <Box
        ref={chatRef}
        sx={{
          flexGrow: 1,
          overflow: 'auto',
          overflowX: 'hidden',
          minWidth: 0,
        }}
      >
        <List sx={{ flexGrow: 1, minWidth: 0, overflowX: 'hidden' }}>
          {/* Introductory message */}
          {messages.length === 1 && (
            <ListItem sx={{ display: 'flex', alignItems: 'flex-start' }}>
              <Avatar alt="Alfie" size="small" src={alfie} sx={{ mt: 0.5 }} />
              <Box sx={{ minWidth: 0, maxWidth: "100%", ml: 1.75 }}>
                <ReactMarkdown components={chatMarkdownComponents}>
                  {messages[0].text}
                </ReactMarkdown>
                <Stack direction="row" sx={{ flexWrap: 'wrap', mt: 3 }}>
                  {suggestedQuestions.slice(0, 3).map((question, index) => (
                    <Chip
                      key={index}
                      label={question}
                      onClick={() => handleSuggestedQuestion(question)}
                      sx={{ ml: 1, mt: 1 }}
                      color="primary"
                      variant="outlined"
                      size="small"
                    />
                  ))}
                </Stack>
              </Box>
            </ListItem>
          )}

          {/* Chat messages */}
          {messages.slice(1).map((message, index) => (
            <ListItem key={message.id} sx={{ alignItems: 'flex-start', mt: 2 }}>
              {message.sender === 'ai' ? (
                <Avatar alt="Alfie" size="small" src={alfie} sx={{ mt: 0.5 }} />
              ) : (
                <Avatar alt="User" size="small" sx={{ mt: 0.5 }}>U</Avatar>
              )}
              <Box sx={{ flex: "1 1 auto", maxWidth: "100%", minWidth: 0, ml: 1.75 }}>
                {message.sender === 'ai' && message.showBlocks && message.blocks ? (
                  createEnhancedMarkdown(message.text, message.blocks)
                ) : (
                  <ReactMarkdown components={chatMarkdownComponents}>
                    {message.text}
                  </ReactMarkdown>
                )}
                
                {/* Update Lesson Plan Button */}
                {message.sender === 'ai' && message.canUpdateLesson && !message.showBlocks && (
                  <Box sx={{ mt: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<UpdateIcon />}
                      onClick={() => handleUpdateLessonPlan(message.text)}
                      sx={{ fontSize: '0.875rem' }}
                    >
                      Update Lesson Plan
                    </Button>
                  </Box>
                )}

                {/* Follow-up Questions */}
                {message.sender === 'ai' && message.followUps && message.followUps.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                      Follow-up questions:
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                      {message.followUps.map((followUp, index) => (
                        <Chip
                          key={index}
                          label={followUp}
                          variant="outlined"
                          size="small"
                          onClick={() => handleSuggestedQuestion(followUp)}
                          sx={{ 
                            fontSize: '0.875rem', 
                            cursor: 'pointer',
                            '&:hover': { bgcolor: 'action.hover' }
                          }}
                        />
                      ))}
                    </Stack>
                  </Box>
                )}
              </Box>
            </ListItem>
          ))}
          
          {isLoading && (
            <ListItem sx={{ alignItems: 'flex-start', mt: 2 }}>
              <Avatar alt="Alfie" size="small" src={alfie} sx={{ mt: 0.5 }} />
              <Box sx={{ ml: 1.75, display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1rem' }}>
                  Thinking...
                </Typography>
              </Box>
            </ListItem>
          )}
        </List>
      </Box>

      {/* Input - styled like Search.js */}
      <Box sx={{ p: 2 }}>
        <TextField
          ref={inputRef}
          fullWidth
          type="search"
          value={inputMessage}
          onChange={(e) => setInputMessage(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !isLoading) handleSendMessage(); }}
          placeholder="Ask about this lesson..."
          disabled={isLoading}
          sx={{ 
            background: theme.palette.paper?.secondary || theme.palette.background.default, 
            border: "none", 
            py: "10px", 
            pl: "10px", 
            '& .MuiInputBase-root': { pr: "10px" },
            '& .MuiInputBase-input': { fontSize: '1rem' }
          }}
          InputProps={{
            endAdornment: isLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", width: "28px", height: "28px" }}>
                <CircularProgress thickness={2} size={20} />
              </Box>
            ) : (
              <IconButton 
                variant="contained" 
                onClick={handleSendMessage} 
                disabled={!inputMessage.trim()}
                sx={{ ml: "10px", width: "40px", height: "40px" }}
              >
                <SendIcon fontSize="medium" sx={{ color: 'secondary.main' }} />
              </IconButton>
            ),
          }}
        />
      </Box>
    </Box>
  );
};

export default LessonChat;